import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { MarkdownPipe } from 'ngx-markdown';

import { TAdaptedMessageV2, TAdaptedReferenceV3 } from '../../../types/types';

import { ReferenceInjectDirective } from '../../../infrastructure/directives/reference-inject.directive';
import { TableExportDirective } from '../../../infrastructure/directives/table-export.directive';

@Component({
  selector: 'app-conversation-paragraphs',
  templateUrl: './conversation-paragraphs.component.html',
  styleUrls: ['./conversation-paragraphs.component.scss'],
  standalone: true,
  imports: [
    ReferenceInjectDirective,
    MarkdownPipe,
    AsyncPipe,
    TableExportDirective,
  ],
})
export class ConversationParagraphsComponent implements OnDestroy {
  private _message: TAdaptedMessageV2 | null = null;
  private _mermaidCache = new Map<string, boolean>();

  @Output() sourceSelected = new EventEmitter<TAdaptedReferenceV3>();

  @Input() set message(msg: TAdaptedMessageV2) {
    this._message = msg;
    this._mermaidCache.clear();
  }

  get message(): TAdaptedMessageV2 | null {
    return this._message;
  }

  isMermaidComplete(text: string): boolean {
    if (!text) return true;

    // If no mermaid blocks, it's safe to render
    if (!text.includes('```mermaid')) return true;

    // Check cache first to avoid expensive re-computation
    const cached = this._mermaidCache.get(text);
    if (cached !== undefined) return cached;

    // More robust approach: check each mermaid block individually
    let remainingText = text;
    let mermaidBlocksFound = 0;
    let completeMermaidBlocks = 0;

    while (remainingText.includes('```mermaid')) {
      const mermaidStart = remainingText.indexOf('```mermaid');
      mermaidBlocksFound++;

      // Find text after this mermaid opening
      const afterMermaidStart = remainingText.substring(mermaidStart + 10); // '```mermaid'.length = 10

      // Look for closing ```
      const closingIndex = afterMermaidStart.indexOf('\n```');

      if (closingIndex !== -1) {
        completeMermaidBlocks++;
        // Move past this complete block for next iteration
        remainingText = afterMermaidStart.substring(closingIndex + 4); // '\n```'.length = 4
      } else {
        // No closing found - incomplete block
        break;
      }
    }

    const isComplete = mermaidBlocksFound > 0 && completeMermaidBlocks === mermaidBlocksFound;

    // Cache the result to avoid re-computation
    this._mermaidCache.set(text, isComplete);

    return isComplete;
  }

  shouldRenderMarkdown(index: number): boolean {
    const paragraph = this.message?.paragraphs?.[index];
    if (!paragraph) return false;

    // Always check mermaid completion - regardless of processing status
    // If there's an incomplete mermaid, don't render (show loading instead)
    return this.isMermaidComplete(paragraph.text);
  }

  ngOnDestroy() {
    this._mermaidCache.clear();
  }
}
